/**
 * NeoCorePlanillasPage — módulo de planillas.
 *
 * Flujo: buscás al colaborador (asociado) → te muestro las cuentas que ha subido
 * → elegís una → le asignás centro de costo y monto por quincena. Exportás el
 * lote ACH para Interbanco (mismo formato que Retiros, vía lib/loteAch).
 */
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { ArrowsClockwise, DownloadSimple, Plus, TrashSimple, PencilSimple, MagnifyingGlass, Money, CheckCircle, Bank } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { generarLoteAch, descargarLoteEnGrupos, type LoteItem } from '@/lib/loteAch'

type Linea = {
  id: string; asociado_id: string | null; nombre: string; neo: string | null
  cuenta_id: string | null; banco: string | null; tipo_cuenta: string | null; numero_cuenta: string | null
  centro_id: string | null; centro: string | null; monto_quincena: number
}
type Centro = { id: string; nombre: string }
type Cuenta = { id: string; banco: string; tipo_cuenta: string; numero_cuenta: string; estado: string; habilitada: boolean }
type Hit = { id: string; neo: string | null; nombre: string }

const BRAND = 'var(--brand-500, #FF5B04)'
const OK = 'var(--ok, #10b981)'
const DANGER = 'var(--danger, #e5484d)'
const fmtQ = (n: number) => 'Q' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const mask = (n: string | null) => (n ? '••••' + n.slice(-4) : '—')
const hoy = () => new Date().toISOString().slice(0, 10)

export function NeoCorePlanillasPage() {
  const [lineas, setLineas] = useState<Linea[]>([])
  const [centros, setCentros] = useState<Centro[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editor, setEditor] = useState<Editable | null>(null)

  const load = useCallback(async () => {
    setError(null)
    const [l, c] = await Promise.all([supabase.rpc('planilla_listar'), supabase.rpc('planilla_centros')])
    if (l.error || c.error) { setError((l.error || c.error)?.message ?? 'Error'); setLoading(false); return }
    setLineas((l.data as Linea[]) ?? [])
    setCentros((c.data as Centro[]) ?? [])
    setLoading(false)
  }, [])
  useEffect(() => { void load() }, [load])

  const eliminar = async (li: Linea) => {
    if (!confirm(`¿Quitar a ${li.nombre} de la planilla?`)) return
    const { error } = await supabase.rpc('planilla_eliminar', { p_id: li.id })
    if (error) { setError(error.message); return }
    await load()
  }

  const total = useMemo(() => lineas.reduce((s, l) => s + Number(l.monto_quincena || 0), 0), [lineas])
  const listos = useMemo(() => lineas.filter((l) => l.banco && l.numero_cuenta).length, [lineas])

  const exportar = () => {
    const items: LoteItem[] = lineas.map((l) => ({
      banco: l.banco ?? '', tipoCuenta: l.tipo_cuenta, nombre: l.nombre,
      numeroCuenta: l.numero_cuenta ?? '', monto: Number(l.monto_quincena) || 0, concepto: 'Planilla Neocoop',
    }))
    const { lineas: filas, excluidos } = generarLoteAch(items)
    if (excluidos.length > 0) alert('No se incluyeron (falta banco reconocido o cuenta):\n\n' + excluidos.join('\n'))
    if (filas.length === 0) { if (excluidos.length === 0) alert('No hay colaboradores para exportar.'); return }
    const n = descargarLoteEnGrupos(filas, `planilla_lote_interbanco_${hoy().replace(/-/g, '')}`)
    if (n > 1) alert(`El lote tiene ${filas.length} colaboradores. Interbanco acepta máx. 9 por archivo, así que se bajan ${n} archivos (grupos de 9). Subilos uno por uno.`)
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--n-500)' }}>Cargando…</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Money size={20} style={{ color: BRAND }} weight="fill" />
            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--n-900)' }}>Planillas</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--n-500)', marginTop: 2 }}>Pago quincenal del equipo · usa las cuentas verificadas del asociado · lote Interbanco.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={() => void load()}><ArrowsClockwise size={16} /> Actualizar</button>
          <button className="btn" onClick={exportar}><DownloadSimple size={16} /> Exportar lote Interbanco ({listos})</button>
        </div>
      </div>

      {error && <div className="error-banner" style={{ margin: 0 }}>{error}</div>}

      {editor && (
        <EditorPanel centros={centros} value={editor} onClose={() => setEditor(null)} onSaved={async () => { setEditor(null); await load() }} onError={setError} />
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1.3fr 1fr 0.9fr 96px', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--n-100)', fontSize: 11, fontWeight: 700, color: 'var(--n-500)', textTransform: 'uppercase', letterSpacing: .3 }}>
          <span>Colaborador</span><span>Cuenta</span><span>Centro de costo</span><span style={{ textAlign: 'right' }}>Quincena</span><span></span>
        </div>
        {lineas.length === 0 ? (
          <div style={{ padding: 28, textAlign: 'center', color: 'var(--n-400)', fontSize: 13 }}>Todavía no hay colaboradores. Agregá el primero.</div>
        ) : lineas.map((l) => (
          <div key={l.id} style={{ display: 'grid', gridTemplateColumns: '1.6fr 1.3fr 1fr 0.9fr 96px', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--n-100)', alignItems: 'center' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--n-800)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cap(l.nombre)}</div>
              {l.neo && <div style={{ fontSize: 11, color: 'var(--n-400)' }}>Neo.id {l.neo}</div>}
            </div>
            <div style={{ fontSize: 12, color: l.banco ? 'var(--n-700)' : DANGER, minWidth: 0 }}>
              {l.banco ? <><Bank size={13} style={{ verticalAlign: -2, marginRight: 4 }} />{l.banco} · {(l.tipo_cuenta ?? '').toLowerCase()} · {mask(l.numero_cuenta)}</> : 'sin cuenta'}
            </div>
            <div style={{ fontSize: 12, color: l.centro ? 'var(--n-700)' : 'var(--n-400)' }}>{l.centro ?? '—'}</div>
            <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 600, color: 'var(--n-900)' }}>{fmtQ(l.monto_quincena)}</div>
            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditor(toEditable(l))} title="Editar" style={{ ...iconBtn, color: 'var(--n-500)' }}><PencilSimple size={16} /></button>
              <button onClick={() => eliminar(l)} title="Quitar" style={{ ...iconBtn, color: DANGER }}><TrashSimple size={16} /></button>
            </div>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px' }}>
          <button onClick={() => setEditor(nuevoEditable())} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: BRAND, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            <Plus size={15} weight="bold" /> Agregar colaborador
          </button>
          <div style={{ fontSize: 13, color: 'var(--n-600)' }}>
            Total quincena: <strong style={{ color: 'var(--n-900)', fontSize: 15 }}>{fmtQ(total)}</strong>
            <span style={{ color: 'var(--n-400)', marginLeft: 8 }}>· {fmtQ(total * 2)}/mes</span>
          </div>
        </div>
      </div>

      <p style={{ color: 'var(--n-400)', fontSize: 11, margin: 0 }}>
        Cada colaborador es un asociado; se paga a una de las cuentas que ya subió y verificó. El lote usa el formato ACH de InterBanking (concepto "Planilla Neocoop"); solo entran los que tienen banco reconocido y cuenta.
      </p>
    </div>
  )
}

// --- Editor (buscar asociado → cuenta → centro → monto) --------------------

type Editable = { id: string | null; asociado_id: string | null; nombre: string; cuenta_id: string | null; centro_id: string | null; monto: string }
const nuevoEditable = (): Editable => ({ id: null, asociado_id: null, nombre: '', cuenta_id: null, centro_id: null, monto: '2000' })
const toEditable = (l: Linea): Editable => ({ id: l.id, asociado_id: l.asociado_id, nombre: l.nombre, cuenta_id: l.cuenta_id, centro_id: l.centro_id, monto: String(l.monto_quincena) })

function EditorPanel({ centros, value, onClose, onSaved, onError }: {
  centros: Centro[]; value: Editable; onClose: () => void; onSaved: () => void; onError: (m: string) => void
}) {
  const [ed, setEd] = useState<Editable>(value)
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<Hit[]>([])
  const [cuentas, setCuentas] = useState<Cuenta[]>([])
  const [loadingCtas, setLoadingCtas] = useState(false)
  const [busy, setBusy] = useState(false)

  const cargarCuentas = useCallback(async (asociadoId: string) => {
    setLoadingCtas(true)
    const { data } = await supabase.rpc('planilla_cuentas_asociado', { p_asociado_id: asociadoId })
    setCuentas((data as Cuenta[]) ?? [])
    setLoadingCtas(false)
  }, [])

  useEffect(() => { if (ed.asociado_id) void cargarCuentas(ed.asociado_id) }, [ed.asociado_id, cargarCuentas])

  useEffect(() => {
    if (ed.asociado_id || q.trim().length < 2) { setHits([]); return }
    let alive = true
    const t = setTimeout(async () => {
      const { data } = await supabase.rpc('notif_buscar_asociado', { p_q: q.trim() })
      if (alive) setHits((data as Hit[]) ?? [])
    }, 250)
    return () => { alive = false; clearTimeout(t) }
  }, [q, ed.asociado_id])

  const guardar = async () => {
    if (!ed.asociado_id) { onError('Elegí un colaborador.'); return }
    setBusy(true)
    const { error } = await supabase.rpc('planilla_upsert', {
      p_id: ed.id, p_asociado_id: ed.asociado_id, p_cuenta_id: ed.cuenta_id,
      p_centro_id: ed.centro_id, p_monto: Number(ed.monto) || 0,
    })
    setBusy(false)
    if (error) { onError(error.message); return }
    onSaved()
  }

  return (
    <div className="card" style={{ padding: 16, border: '1px solid ' + BRAND }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--n-900)' }}>{ed.id ? 'Editar colaborador' : 'Agregar colaborador'}</span>
        <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--n-500)', cursor: 'pointer', fontSize: 13 }}>Cancelar</button>
      </div>

      {/* 1. Colaborador */}
      {ed.asociado_id ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 9, background: 'var(--n-50,#f7f7f8)', marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--n-800)' }}>{cap(ed.nombre)}</span>
          {!ed.id && <button onClick={() => { setEd({ ...ed, asociado_id: null, nombre: '', cuenta_id: null }); setCuentas([]) }} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--n-500)', cursor: 'pointer', fontSize: 12 }}>cambiar</button>}
        </div>
      ) : (
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <MagnifyingGlass size={15} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--n-400)' }} />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar colaborador por nombre o Neo.id…" style={{ ...inp, paddingLeft: 30 }} />
          {hits.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 4, background: 'var(--surface,#fff)', border: '1px solid var(--n-200)', borderRadius: 9, boxShadow: '0 8px 24px rgba(0,0,0,.1)', maxHeight: 240, overflowY: 'auto' }}>
              {hits.map((h) => (
                <button key={h.id} onClick={() => { setEd({ ...ed, asociado_id: h.id, nombre: h.nombre, cuenta_id: null }); setHits([]) }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '8px 10px', background: 'none', border: 'none', borderBottom: '1px solid var(--n-100)', cursor: 'pointer' }}>
                  <span style={{ fontSize: 13, color: 'var(--n-800)', flex: 1 }}>{cap(h.nombre)}</span>
                  {h.neo && <span style={{ fontSize: 11, color: 'var(--n-400)' }}>{h.neo}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 2. Cuenta (las que subió el asociado) */}
      {ed.asociado_id && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--n-500)', textTransform: 'uppercase', marginBottom: 6 }}>Cuenta a la que se le paga</div>
          {loadingCtas ? <div style={{ fontSize: 12, color: 'var(--n-400)' }}>Cargando cuentas…</div>
            : cuentas.length === 0 ? <div style={{ fontSize: 12, color: DANGER }}>Este asociado no tiene cuentas subidas. Que la registre y verifique en la app.</div>
            : cuentas.map((c) => (
              <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 9, border: '1px solid ' + (ed.cuenta_id === c.id ? BRAND : 'var(--n-200)'), marginBottom: 6, cursor: 'pointer', background: ed.cuenta_id === c.id ? 'var(--brand-50,#fff5ee)' : 'var(--surface,#fff)' }}>
                <input type="radio" checked={ed.cuenta_id === c.id} onChange={() => setEd({ ...ed, cuenta_id: c.id })} />
                <Bank size={15} style={{ color: 'var(--n-600)' }} />
                <span style={{ fontSize: 13, color: 'var(--n-800)', fontWeight: 600 }}>{c.banco}</span>
                <span style={{ fontSize: 12, color: 'var(--n-500)' }}>{c.tipo_cuenta.toLowerCase()} · {c.numero_cuenta}</span>
                {c.habilitada && <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700, color: OK }}><CheckCircle size={12} weight="fill" /> verificada</span>}
              </label>
            ))}
        </div>
      )}

      {/* 3. Centro de costo + monto */}
      {ed.asociado_id && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--n-500)' }}>
            Centro de costo / unidad
            <select value={ed.centro_id ?? ''} onChange={(e) => setEd({ ...ed, centro_id: e.target.value || null })} style={{ ...inp, width: 200 }}>
              <option value="">— sin asignar —</option>
              {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: 'var(--n-500)' }}>
            Monto por quincena
            <input value={ed.monto} onChange={(e) => setEd({ ...ed, monto: e.target.value })} inputMode="decimal" style={{ ...inp, width: 130, textAlign: 'right', fontWeight: 600 }} />
          </label>
          <button className="btn" disabled={busy || !ed.asociado_id} onClick={guardar}>{busy ? 'Guardando…' : 'Guardar'}</button>
        </div>
      )}
    </div>
  )
}

const cap = (s: string | null) => (s ? s.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') : '')
const inp: CSSProperties = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--n-200)', fontSize: 13, width: '100%', background: 'var(--surface,#fff)', color: 'var(--n-800)', boxSizing: 'border-box' }
const iconBtn: CSSProperties = { background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'inline-flex', alignItems: 'center' }

export default NeoCorePlanillasPage
